if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          select: "select",
        };
        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
      }

      connectedCallback() {
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");

        // FIX #1: stop safely if the core parts of the page are missing
        if (!this.container || !this.mainProduct || !this.mainProductForm) return;

        this.mainAddToCart = this.mainProductForm.querySelector(".m-add-to-cart");
        this.mainDynamicCheckout = this.mainProductForm.querySelector(this.selectors.buyNowBtn);
        this.disableSelectedVariantDefault = this.mainProduct.dataset?.disableSelectedVariantDefault === "true";
        this.domNodes = queryDomNodes(this.selectors, this.container) || {};

        this.init();
      }

      init() {
        if (!this.mainAddToCart) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        this.variantData = this.getVariantData();

        // FIX #5: set up syncing early, so the button text always updates (prevents stuck "Sold Out")
        this.syncWithMainProductForm();

        const headerHeight = (window.MinimogSettings && MinimogSettings.headerHeight) || 66;
        const rootMargin = `${headerHeight}px 0px 0px 0px`;

        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio !== 1) {
                  this.container.style.setProperty("--m-translate-y", 0);
                } else {
                  this.container.style.setProperty("--m-translate-y", "100%");
                }
                document.documentElement.classList[entry.intersectionRatio !== 1 ? "add" : "remove"]("stick-atc-show");
              });
            },
            { threshold: 1, rootMargin }
          );
        }

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        // FIX #1: only add click events if the title and image exist
        const { prodTitle, mainImage } = this.domNodes;
        if (prodTitle) prodTitle.addEventListener("click", () => __scrollToTop());
        if (mainImage) mainImage.addEventListener("click", () => __scrollToTop());

        this.handleCustomFields();
        this.setObserveTarget();

        const mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        mql.onchange = this.setStickyAddToCartHeight.bind(this);
        this.setStickyAddToCartHeight();

        // FIX #1: only set up the dropdown if it exists
        if (this.domNodes.select) {
          this.domNodes.select.addEventListener("change", (e) => this.handleSelectChange(e));
        }
      }

      handleSelectChange(e) {
        const { target } = e;
        const variantPicker = this.mainProduct.querySelector("variant-picker");
        const variantIdInput = this.querySelector(this.selectors.variantIdSelect);
        const selectedVariantId = variantIdInput ? variantIdInput.value : null;

        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
        this.currentVariant
          ? this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut)
          : this.toggleAddButton(true, window.MinimogStrings.unavailable);

        // FIX #1: stop safely if the selected option has no option data
        const selectedOption = target.options ? target.options[target.selectedIndex] : null;
        if (!selectedOption || !selectedOption.dataset.options) return;

        const selectedOptionIds = selectedOption.dataset.options.split(",").filter((id) => id);

        if (variantPicker) {
          selectedOptionIds.forEach((optionId) => {
            const input = variantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
            // FIX #1: skip any option that can't be found on the main product
            if (!input) return;

            switch (input.tagName) {
              case "OPTION": {
                const inputParent = input.parentNode; // select tag
                if (inputParent) inputParent.value = input.value;
                break;
              }
              case "INPUT":
                input.checked = true;
                break;
            }
          });
          variantPicker.dispatchEvent(new Event("change"));
        }
      }

      getVariantData() {
        if (this.variantData) return this.variantData;

        // FIX #1: return an empty list instead of crashing if product data is missing or broken
        const dataScript = this.container.querySelector('[type="application/json"]');
        if (!dataScript) return [];

        try {
          this.variantData = JSON.parse(dataScript.textContent);
        } catch (err) {
          this.variantData = [];
        }
        return this.variantData;
      }

      setObserveTarget() {
        if (this.observer && this.mainProductForm) {
          this.observer.observe(this.mainProductForm);
          this.observeTarget = this.mainProductForm;
        }
      }

      setUnavailable() {
        const productForm = this.querySelector(".m-product-form");
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');

        if (addButton) {
          this.toggleAddButton(true, window.MinimogStrings.unavailable);
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector(".m-product-form");
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          if (addButtonText) addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      handleDisableSelectedVariantDefault() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        const pickerFields = this.querySelector(".m-product-option--dropdown-select");
        // FIX #1: only reset the dropdown if it exists
        if (pickerFields) pickerFields.value = "";
        this.setUnavailable();
      }

      setStickyAddToCartHeight() {
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", this.offsetHeight + "px");
        if (window.MinimogSettings) window.MinimogSettings.stickyAddToCartHeight = this.offsetHeight;
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          const isMainProduct = e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;
          this.currentVariant = e.data.variant;
          if (this.currentVariant) {
            if (variantInput) variantInput.value = e.data.variant.id;
            this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut);
          } else {
            if (variantInput) variantInput.value = "";
            this.toggleAddButton(true, window.MinimogStrings.unavailable);
          }
        });
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } = this.domNodes;

        // FIX #2: check the custom fields at the moment of clicking, not only on page load.
        // Returns the first field that is still missing or invalid, or null if everything is filled in.
        const getMissingField = () => {
          const fields = document.querySelectorAll(".m-main-product--info .m-product-custom-field .form-field");
          for (const field of fields) {
            if (typeof field.checkValidity === "function" && !field.checkValidity()) return field;
          }
          return null;
        };

        // FIX #2: send the customer to the missing field and show the browser's "please fill in" message
        const showMissingField = (field) => {
          __scrollToTop(this.mainProductInfo, () => {
            field.focus();
            if (typeof field.reportValidity === "function") field.reportValidity();
          });
        };

        // FIX #2: always route the sticky Add to Cart through the main form when the product has custom fields,
        // so the customer's custom text or upload is always included with the item.
        if (addToCart && this.mainAddToCart) {
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            const missingField = getMissingField();
            if (missingField) {
              // Something is missing: scroll up and show which field needs filling in
              showMissingField(missingField);
            } else {
              // Everything is filled in: add to cart straight away, no scrolling
              this.mainAddToCart.click();
            }
          });
        }

        if (buyNowBtn && this.mainDynamicCheckout) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missingField = getMissingField();
              if (missingField) {
                e.preventDefault();
                e.stopPropagation();
                showMissingField(missingField);
              }
            },
            true
          );
        }

        if (foxkitBtn && this.mainAddToCart) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            const missingField = getMissingField();
            if (missingField) {
              showMissingField(missingField);
            } else {
              this.mainAddToCart.click();
            }
          });
        }
      }
    }
  );
}